import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ComponentType,
  type ReactNode,
} from "react";
import { useNavigate } from "react-router-dom";
import { Button, ConfirmModal } from "@a2z/ui";

import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import type { CategoryView, ProductView } from "@/lib/api/types";
import type { Locale } from "@/lib/i18n/config";
import { pickLocalized } from "@/lib/i18n/localized";
import { getSellerPath } from "@/lib/routing/portal-urls";
import { CategoryForm } from "@/modules/categories/components/CategoryForm";
import { CategoryModalShell } from "@/modules/categories/components/redesign/category-modal";
import { CategoryCard } from "@/modules/categories/components/CategoryCard";
import { CategoriesTableView } from "@/modules/categories/components/CategoriesTableView";
import { CategoryStatsStrip } from "@/modules/categories/components/CategoryStatsStrip";
import { useDeleteCategoryMutation } from "@/modules/categories/services/useDeleteCategoryMutation";
import { useProductsQuery } from "@/modules/products/services/useProductsQuery";
import { usePageChrome } from "@/shared/layout/PageChromeContext";
import { useIsMobile } from "@/shared/hooks/useIsMobile";
import { MobileHeaderNotificationBell } from "@/modules/notifications/components/MobileHeaderNotificationBell";
import { MobileFade } from "@/shared/components/MobileFade";
import { PageTransition } from "@/shared/components/PageTransition";

type Props = {
  locale: Locale;
  storeSlug: string;
  categories: CategoryView[];
  showSaveNotice?: boolean;
  defaultCreateOpen?: boolean;
  defaultEditId?: string | null;
};

type ViewMode = "grid" | "table";

const folderIcon = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
  </svg>
);

export function CategoriesClient({
  locale,
  storeSlug,
  categories,
  showSaveNotice = false,
  defaultCreateOpen = false,
  defaultEditId = null,
}: Props) {
  const t = useTranslations();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { setHero, setHeroAux } = usePageChrome();
  const categoriesHref = `${getSellerPath(locale, storeSlug)}/categories`;

  const [isCreateOpen, setIsCreateOpen] = useState(defaultCreateOpen);
  const [editingCategoryId, setEditingCategoryId] = useState<string | null>(
    defaultEditId,
  );
  const [pendingDelete, setPendingDelete] = useState<CategoryView | null>(null);
  const [savedNotice, setSavedNotice] = useState(showSaveNotice);
  const [view, setView] = useState<ViewMode>("grid");
  const [actionError, setActionError] = useState<string | null>(null);

  const { mutateAsync: deleteCategory, isPending: isDeleting } =
    useDeleteCategoryMutation();

  // The page renders its own title/toolbar inline (like Orders / Reviews), so
  // clear the shared hero chrome.
  useEffect(() => {
    setHero(null);
    setHeroAux(null);
    return () => {
      setHero(null);
      setHeroAux(null);
    };
  }, [setHero, setHeroAux]);

  const editingCategory = useMemo(
    () =>
      categories.find((category) => category.id === editingCategoryId) ?? null,
    [categories, editingCategoryId],
  );

  const sortedCategories = useMemo(() => {
    return [...categories].sort((a, b) => {
      if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
      return a.slug.localeCompare(b.slug);
    });
  }, [categories]);

  // Pull a slice of products to derive per-category counts, catalog share, and
  // the uncategorized stat. 200 is generous for the storefront catalog.
  const { data: productsData } = useProductsQuery({ storeSlug, limit: 200 });
  const counts = useMemo(() => {
    const map = new Map<string, number>();
    productsData?.items.forEach((product: ProductView) => {
      product.categoryIds.forEach((categoryId) => {
        map.set(categoryId, (map.get(categoryId) ?? 0) + 1);
      });
    });
    return map;
  }, [productsData]);
  const totalProducts = productsData?.total ?? 0;
  const uncategorizedCount = useMemo(
    () =>
      productsData?.items.filter((p: ProductView) => p.categoryIds.length === 0)
        .length ?? 0,
    [productsData],
  );
  const categorizedCount = Math.max(0, totalProducts - uncategorizedCount);
  const topCategory = useMemo(() => {
    let best: { name: string; count: number } | null = null;
    for (const category of categories) {
      const count = counts.get(category.id) ?? 0;
      if (!best || count > best.count) {
        best = {
          name:
            pickLocalized(locale, category.nameAr, category.nameEn) ??
            category.nameEn ??
            category.nameAr,
          count,
        };
      }
    }
    return best;
  }, [categories, counts, locale]);

  useEffect(() => {
    if (editingCategoryId && !editingCategory && defaultEditId) {
      navigate(categoriesHref, { replace: true });
    }
  }, [categoriesHref, defaultEditId, editingCategory, editingCategoryId, navigate]);

  useEffect(() => {
    if (!savedNotice) return;
    const id = setTimeout(() => setSavedNotice(false), 3000);
    return () => clearTimeout(id);
  }, [savedNotice]);

  const closeModal = () => {
    setIsCreateOpen(false);
    setEditingCategoryId(null);
    navigate(categoriesHref, { replace: true });
  };
  const handleSuccess = () => {
    setSavedNotice(true);
    closeModal();
  };
  const openCreate = useCallback(() => {
    setEditingCategoryId(null);
    setIsCreateOpen(true);
  }, []);
  const openEdit = (categoryId: string) => {
    setIsCreateOpen(false);
    setEditingCategoryId(categoryId);
  };

  const handleConfirmDelete = async () => {
    if (!pendingDelete) return;
    setActionError(null);
    try {
      await deleteCategory({ storeSlug, categoryId: pendingDelete.id });
      if (editingCategoryId === pendingDelete.id) setEditingCategoryId(null);
      setPendingDelete(null);
    } catch (caughtError) {
      setActionError(
        caughtError instanceof Error
          ? caughtError.message
          : t(keys.errors.generic),
      );
    }
  };

  const categoriesTitle = t(keys.seller.categoriesTitle);
  const pageSubtitle = t(keys.categories.pageSubtitle);
  const addCategoryLabel = t(keys.seller.addCategoryAction);

  const viewToggle = (
    <div className="flex shrink-0 items-center gap-0.5 rounded-[11px] border border-[color:var(--line2)] bg-[color:var(--surface-alt)] p-[3px]">
      {(["grid", "table"] as const).map((option) => {
        const active = view === option;
        return (
          <button
            key={option}
            type="button"
            onClick={() => setView(option)}
            aria-pressed={active}
            aria-label={t(
              option === "grid" ? keys.categories.viewGrid : keys.categories.viewTable,
            )}
            className={[
              "inline-flex items-center gap-[6px] rounded-[8px] px-[11px] py-[6px] text-[12.5px] font-semibold transition-colors",
              active
                ? "bg-[color:var(--card)] text-[color:var(--text-primary)] shadow-[0_1px_2px_rgba(0,0,0,0.06)]"
                : "text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]",
            ].join(" ")}
          >
            {option === "grid" ? <GridIcon /> : <TableIcon />}
            <span className="hidden sm:inline">
              {t(option === "grid" ? keys.categories.viewGrid : keys.categories.viewTable)}
            </span>
          </button>
        );
      })}
    </div>
  );

  const grid = (
    <div className="grid gap-3.5 [grid-template-columns:repeat(auto-fill,minmax(min(100%,248px),1fr))]">
      {sortedCategories.map((category) => (
        <CategoryCard
          key={category.id}
          locale={locale}
          category={category}
          productCount={counts.get(category.id) ?? 0}
          totalProducts={totalProducts}
          onEdit={() => openEdit(category.id)}
          onDelete={() => setPendingDelete(category)}
        />
      ))}
    </div>
  );

  const emptyState = (
    <div className="flex flex-col items-center gap-2 rounded-[18px] border border-[color:var(--border-light)] bg-[color:var(--surface)] py-14 text-center">
      <p className="text-[15px] font-semibold text-[color:var(--text-primary)]">
        {t(keys.common.emptyTitle)}
      </p>
      <p className="px-6 text-[13px] text-[color:var(--text-muted)]">
        {t(keys.seller.emptyCategoriesHint)}
      </p>
      <div className="mt-2">
        <Button type="button" variant="secondary" onClick={openCreate}>
          {addCategoryLabel}
        </Button>
      </div>
    </div>
  );

  const savedBanner = savedNotice ? (
    <div
      className="flex items-center gap-2.5 rounded-[10px] border border-[color:var(--success-100)] bg-[color:var(--success-100)] px-4 py-2.5 text-[13px] text-[color:var(--success-500)]"
      role="status"
    >
      <span aria-hidden="true">{"✓"}</span>
      <span className="flex-1">{t(keys.seller.categorySavedNotice)}</span>
    </div>
  ) : null;

  const errorBanner = actionError ? (
    <div
      className="rounded-md border border-[color:var(--error-500)] bg-[color:var(--error-100)] px-4 py-3 text-sm text-[color:var(--error-500)]"
      role="alert"
    >
      {actionError}
    </div>
  ) : null;

  const BodyWrap: ComponentType<{ children: ReactNode; className?: string }> =
    isMobile ? MobileFade : PageTransition;

  return (
    <>
      {isMobile ? (
        <div
          className="sticky top-0 z-30 border-b border-[color:var(--hair)] bg-[color:var(--canvas)] px-4"
          style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 14px)" }}
        >
          <div className="flex items-center justify-between gap-2 px-1 pb-2 pt-1">
            <h1 className="text-[28px] font-extrabold tracking-[-0.025em] text-[color:var(--text-primary)]">
              {categoriesTitle}
            </h1>
            <div className="flex items-center gap-1 rounded-full bg-[color:var(--soft2)] p-[5px]">
              <button
                type="button"
                onClick={openCreate}
                aria-label={addCategoryLabel}
                className="flex h-[30px] w-[33px] items-center justify-center rounded-full text-[color:var(--text-secondary)] transition-colors hover:bg-[color:var(--soft)]"
              >
                <PlusIcon />
              </button>
              <span className="h-[17px] w-px bg-[color:var(--border-strong)]" />
              <MobileHeaderNotificationBell triggerClassName="relative flex h-[30px] w-[33px] items-center justify-center rounded-full text-[color:var(--text-secondary)] transition-colors hover:bg-[color:var(--soft)]" />
            </div>
          </div>
        </div>
      ) : null}

      <BodyWrap className="w-full">
        <div
          className={[
            "flex w-full flex-col gap-5",
            isMobile ? "px-4 pb-24 pt-4" : "px-7 py-6",
          ].join(" ")}
        >
          {!isMobile ? (
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="min-w-0">
                <h1 className="text-[24px] font-bold leading-tight tracking-[-0.01em] text-[color:var(--text-primary)]">
                  {categoriesTitle}
                </h1>
                <p className="mt-1 text-[13.5px] text-[color:var(--text-muted)]">
                  {pageSubtitle}
                </p>
              </div>
              <button
                type="button"
                onClick={openCreate}
                className="inline-flex shrink-0 items-center gap-2 rounded-[11px] bg-[color:var(--accent-500)] px-4 py-2.5 text-[13.5px] font-semibold text-[color:var(--on-accent)]"
              >
                <PlusIcon />
                {addCategoryLabel}
              </button>
            </div>
          ) : null}

          {savedBanner}
          {errorBanner}

          <CategoryStatsStrip
            totalProducts={totalProducts}
            categoriesCount={categories.length}
            categorizedCount={categorizedCount}
            topCategory={topCategory}
            uncategorizedCount={uncategorizedCount}
            variant={isMobile ? "mobile" : "desktop"}
          />

          {sortedCategories.length === 0 ? (
            emptyState
          ) : isMobile ? (
            grid
          ) : (
            <div className="rounded-[20px] border border-[color:var(--line2)] bg-[color:var(--card)] p-3 sm:p-4">
              <div className="mb-4 flex items-center justify-end">
                {viewToggle}
              </div>
              {view === "table" ? (
                <CategoriesTableView
                  locale={locale}
                  categories={sortedCategories}
                  counts={counts}
                  totalProducts={totalProducts}
                  onEdit={(category) => openEdit(category.id)}
                  onDelete={(category) => setPendingDelete(category)}
                />
              ) : (
                grid
              )}
            </div>
          )}
        </div>
      </BodyWrap>

      {isCreateOpen ? (
        <CategoryModalShell
          icon={folderIcon}
          title={t(keys.seller.categoriesNewTitle)}
          subtitle={t(keys.seller.categoriesGuidance)}
          onClose={closeModal}
        >
          <CategoryForm
            locale={locale}
            storeSlug={storeSlug}
            mode="create"
            redirectOnSuccess={false}
            enableFloatingActions={false}
            onCancel={closeModal}
            onSuccess={handleSuccess}
          />
        </CategoryModalShell>
      ) : null}

      {editingCategory ? (
        <CategoryModalShell
          icon={folderIcon}
          title={t(keys.seller.categoriesEditTitle)}
          subtitle={
            pickLocalized(locale, editingCategory.nameAr, editingCategory.nameEn) ??
            editingCategory.slug
          }
          onClose={closeModal}
        >
          <CategoryForm
            locale={locale}
            storeSlug={storeSlug}
            mode="edit"
            categoryId={editingCategory.id}
            redirectOnSuccess={false}
            enableFloatingActions={false}
            onCancel={closeModal}
            onSuccess={handleSuccess}
            defaultValues={{
              nameAr: editingCategory.nameAr,
              nameEn: editingCategory.nameEn,
              sortOrder: editingCategory.sortOrder,
            }}
          />
        </CategoryModalShell>
      ) : null}

      <ConfirmModal
        tone="danger"
        isOpen={pendingDelete !== null}
        onClose={() => setPendingDelete(null)}
        onConfirm={() => void handleConfirmDelete()}
        title={t(keys.categories.deleteConfirmTitle)}
        description={t(keys.categories.deleteConfirmBody)}
        confirmLabel={t(keys.common.delete)}
        cancelLabel={t(keys.common.cancel)}
        loadingLabel={t(keys.common.loading)}
        closeLabel={t(keys.common.close)}
        isSubmitting={isDeleting}
        entity={{
          icon: folderIcon,
          label: t(keys.seller.categoriesTitle),
          value: pendingDelete
            ? (pickLocalized(locale, pendingDelete.nameAr, pendingDelete.nameEn) ??
              pendingDelete.slug)
            : undefined,
        }}
      />

    </>
  );
}

function PlusIcon({ size = 17, stroke = 2 }: { size?: number; stroke?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

function GridIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="3" width="7" height="7" rx="1.5" />
      <rect x="14" y="3" width="7" height="7" rx="1.5" />
      <rect x="14" y="14" width="7" height="7" rx="1.5" />
      <rect x="3" y="14" width="7" height="7" rx="1.5" />
    </svg>
  );
}

function TableIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <path d="M3 9h18M3 15h18M9 3v18" />
    </svg>
  );
}
